"use client";

import { useEffect, useState, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { NeoLogo } from "@/components/ui/neo-logo";
import { useAuthState } from "@/hooks/use-auth-state";
import { Loader2, ArrowRight, Sparkles, Zap, Shield } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SignupForm } from "@/components/auth/signup-form";
import { ForgotPasswordForm } from "@/components/auth/forgot-password-form";
import { useToast } from "@/hooks/use-toast";
import { createAndNavigateToNewSession } from "@/lib/services/sessionManager";
import LanguageSwitcher from "@/components/common/LanguageSwitcher/LanguageSwitcher";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { useTranslations } from "next-intl";
import { useAgentService } from "@/hooks/services/use-agent-service";

// Module-level flag to prevent multiple redirection attempts
let hasAttemptedInitialRedirect = false;

interface HomePageProps {
  locale: string;
}

export default function HomePage({ locale }: HomePageProps) {
  const authState = useAuthState();
  const user = authState.user;
  const status = authState.sessionStatus;
  const router = useRouter();
  const searchParams = useSearchParams();
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Use next-intl hook for translations
  const t = useTranslations("HomePage");

  // Dynamically generate current year
  const currentYear = new Date().getFullYear();

  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [
    isInitializingDefaultAgentSession,
    setIsInitializingDefaultAgentSession,
  ] = useState(false);

  const { toast } = useToast();
  const { getDefaultAgent } = useAgentService();

  useEffect(() => {
    setMounted(true);
    // Reset flag on component mount if not authenticated, allowing a fresh attempt after login
    if (status !== "authenticated") {
      hasAttemptedInitialRedirect = false;
    }
  }, [status]);

  const clearAuthQueryParams = useCallback(() => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete("showLogin");
    newParams.delete("showSignup");
    newParams.delete("showForgotPassword");
    newParams.delete("signupSuccess");
    newParams.delete("redirectedFrom");
    router.replace(`/${locale}?${newParams.toString()}`, { scroll: false });
  }, [router, searchParams, locale]);

  // useEffect for managing auth modal visibility based on query params
  useEffect(() => {
    const showLogin = searchParams.get("showLogin") === "true";
    const showSignup = searchParams.get("showSignup") === "true";
    const showForgotPassword =
      searchParams.get("showForgotPassword") === "true";
    const signupSuccess = searchParams.get("signupSuccess") === "true";

    if (showLogin) {
      router.push(`/${locale}/login`);
    } else if (showSignup) {
      setShowSignupModal(true);
    } else if (showForgotPassword) {
      setShowForgotPasswordModal(true);
    } else if (signupSuccess) {
      toast({
        title: t("signupSuccessTitle"),
        description: t("signupSuccessDescription"),
      });
      clearAuthQueryParams();
    }
  }, [searchParams, clearAuthQueryParams, toast, t, router, locale]);

  // useEffect for automatic redirection on load for authenticated users
  useEffect(() => {
    // Check if user is trying to access auth modals
    const showLogin = searchParams.get("showLogin") === "true";
    const showSignup = searchParams.get("showSignup") === "true";
    const showForgotPassword =
      searchParams.get("showForgotPassword") === "true";

    if (
      status === "authenticated" &&
      user &&
      !hasAttemptedInitialRedirect &&
      !isInitializingDefaultAgentSession // Don't redirect if user is trying to access auth modals
    ) {
      hasAttemptedInitialRedirect = true;
      setIsInitializingDefaultAgentSession(true);

      const initiateInitialRedirect = async () => {
        try {
          const defaultAgent = await getDefaultAgent();
          if (defaultAgent && defaultAgent.id) {
            await createAndNavigateToNewSession(defaultAgent.id, {
              router,
              sessionName: t("defaultAgentChatName"),
              onSuccess: () => {},
              onError: error => {
                console.error(
                  `HomePage Effect: Failed to create session for default agent ${defaultAgent.id}:`,
                  error
                );
                toast({
                  title: t("sessionStartErrorTitle"),
                  description: t("sessionStartErrorDescription"),
                  variant: "default",
                });
                setIsInitializingDefaultAgentSession(false);
                router.push("/agents");
              },
            });
          } else {
            toast({
              title: t("defaultAgentUnavailableTitle"),
              description: t("defaultAgentUnavailableDescription1"),
              variant: "default",
            });
            setIsInitializingDefaultAgentSession(false);
            router.push("/agents");
          }
        } catch (error) {
          console.error(
            "HomePage Effect: Error during initial default agent session creation:",
            error
          );
          toast({
            title: t("errorTitle"),
            description: t("errorPreparingSessionDescription"),
            variant: "destructive",
          });
          setIsInitializingDefaultAgentSession(false);
          router.push("/agents");
        }
      };
      initiateInitialRedirect();
    }
  }, [
    user,
    status,
    router,
    toast,
    isInitializingDefaultAgentSession,
    t,
    searchParams,
  ]);

  // Handler for the "Get Started" button click
  const handleGetStartedClick = async () => {
    if (!user?.id) {
      router.push(`/${locale}?showLogin=true`);
      return;
    }
    setIsInitializingDefaultAgentSession(true);
    try {
      const defaultAgent = await getDefaultAgent();
      if (defaultAgent && defaultAgent.id) {
        await createAndNavigateToNewSession(defaultAgent.id, {
          router,
          sessionName: t("defaultAgentChatName"),
          onSuccess: () => {},
          onError: error => {
            console.error(
              `HomePage Button: Failed to create session for default agent ${defaultAgent.id}:`,
              error
            );
            toast({
              title: t("errorCreatingSessionTitle"),
              description:
                error.message || t("couldNotStartNewChatDescription"),
              variant: "destructive",
            });
            setIsInitializingDefaultAgentSession(false);
          },
        });
      } else {
        toast({
          title: t("defaultAgentUnavailableTitle"),
          description: t("defaultAgentUnavailableDescription2"),
          variant: "default",
        });
        setIsInitializingDefaultAgentSession(false);
        router.push("/agents");
      }
    } catch (error) {
      console.error(
        "HomePage Button: Error during default agent session creation:",
        error
      );
      toast({
        title: t("errorTitle"),
        description: t("errorPreparingSessionDescription"),
        variant: "destructive",
      });
      setIsInitializingDefaultAgentSession(false);
      router.push("/agents");
    }
  };

  const handleSignupModalOpenChange = (open: boolean) => {
    setShowSignupModal(open);
    if (!open) clearAuthQueryParams();
  };

  const handleForgotPasswordModalOpenChange = (open: boolean) => {
    setShowForgotPasswordModal(open);
    if (!open) clearAuthQueryParams();
  };

  // The ONLY login entry: the organisation-aware page. The legacy global
  // login form is no longer reachable as a standalone surface (OrgLogin
  // still embeds it for local-credential organisations).
  const openLoginModal = () => {
    setShowSignupModal(false);
    setShowForgotPasswordModal(false);
    router.push(`/${locale}/login`);
  };

  const openSignupModal = () => {
    setShowForgotPasswordModal(false);
    setShowSignupModal(true);
  };

  const openForgotPasswordModal = () => {
    setShowSignupModal(false);
    setShowForgotPasswordModal(true);
  };

  if (!mounted) {
    return null;
  }

  return (
    <>
      <div className="flex min-h-screen flex-col">
        {/* Modern Header with Glass Effect */}
        <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container flex h-16 max-w-screen-2xl items-center px-6 lg:px-8">
            <Link className="flex items-center space-x-3" href="/">
              <NeoLogo className="h-10 w-10" priority={true} />
              <span className="hidden text-xl font-bold sm:inline-block">
                Neo
              </span>
            </Link>
            <nav className="ml-auto flex items-center space-x-4">
              <LanguageSwitcher />
              {status === "authenticated" && user ? (
                <Button asChild variant="default" size="sm">
                  <Link href="/agents">{t("dashboardButton")}</Link>
                </Button>
              ) : (
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm" onClick={openLoginModal}>
                    {t("loginButton")}
                  </Button>
                  <Button size="sm" onClick={openSignupModal}>
                    {t("signUpButton")}
                  </Button>
                </div>
              )}
            </nav>
          </div>
        </header>

        <main className="flex-1 pb-[68px] sm:pb-[72px]">
          {/* Modern Hero Section with Gradient Background */}
          <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-muted/20">
            {/* Background Pattern */}
            <div className="bg-grid-white/[0.02] absolute inset-0 bg-[size:50px_50px]" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />

            <div className="container relative mx-auto px-4 py-24 sm:py-32 lg:py-40">
              <div className="mx-auto max-w-4xl text-center">
                {/* Logo Animation Container */}
                <div className="mb-8 flex justify-center">
                  <div className="relative">
                    <div className="absolute -inset-4 animate-pulse rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 blur-xl" />
                    <div className="relative rounded-full border border-border/50 bg-background/80 p-6 shadow-2xl backdrop-blur-sm">
                      <NeoLogo
                        className="h-16 w-16 sm:h-20 sm:w-20"
                        priority={true}
                      />
                    </div>
                  </div>
                </div>

                {/* Hero Content */}
                <div className="space-y-6">
                  <h1 className="text-4xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
                    <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                      {t("heroTitle")}
                    </span>
                  </h1>

                  <p className="mx-auto max-w-2xl text-lg text-muted-foreground sm:text-xl">
                    {t("heroSubtitle")}
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-col items-center justify-center gap-4 pt-8 sm:flex-row">
                    {status === "authenticated" && user ? (
                      <Button
                        size="lg"
                        onClick={handleGetStartedClick}
                        disabled={isInitializingDefaultAgentSession}
                        className="h-12 px-8 text-base font-medium shadow-lg transition-all duration-200 hover:shadow-xl"
                      >
                        {isInitializingDefaultAgentSession ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            {t("initializingSession")}
                          </>
                        ) : (
                          <>
                            {t("getStartedButton")}
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="lg"
                          onClick={openSignupModal}
                          className="h-12 bg-gradient-to-r from-primary to-primary/90 px-8 text-base font-medium shadow-lg transition-all duration-200 hover:from-primary/90 hover:to-primary hover:shadow-xl"
                        >
                          {t("getStartedButton")}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="lg"
                          onClick={openLoginModal}
                          className="h-12 border-2 px-8 text-base font-medium transition-all duration-200 hover:bg-muted/50"
                        >
                          {t("loginButton")}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-24 sm:py-32">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-2xl text-center">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                  {t("featuresTitle")}
                </h2>
              </div>

              <div className="mx-auto mt-16 max-w-5xl">
                <div className="grid gap-8 md:grid-cols-3">
                  <div className="group relative overflow-hidden rounded-2xl border bg-card p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Sparkles className="h-6 w-6" />
                    </div>
                    <h3 className="mb-3 text-xl font-semibold">
                      {t("feature1Title")}
                    </h3>
                    <p className="leading-relaxed text-muted-foreground">
                      {t("feature1Description")}
                    </p>
                  </div>

                  <div className="group relative overflow-hidden rounded-2xl border bg-card p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Zap className="h-6 w-6" />
                    </div>
                    <h3 className="mb-3 text-xl font-semibold">
                      {t("feature2Title")}
                    </h3>
                    <p className="leading-relaxed text-muted-foreground">
                      {t("feature2Description")}
                    </p>
                  </div>

                  <div className="group relative overflow-hidden rounded-2xl border bg-card p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Shield className="h-6 w-6" />
                    </div>
                    <h3 className="mb-3 text-xl font-semibold">
                      {t("feature3Title")}
                    </h3>
                    <p className="leading-relaxed text-muted-foreground">
                      {t("feature3Description")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative overflow-hidden bg-gradient-to-r from-primary to-primary/90 py-24 sm:py-32">
            <div className="bg-grid-white/[0.05] absolute inset-0 bg-[size:30px_30px]" />
            <div className="container relative mx-auto px-4">
              <div className="mx-auto max-w-2xl text-center">
                <h2 className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl lg:text-5xl">
                  {t("ctaTitle")}
                </h2>
                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
                  <Button
                    size="lg"
                    variant="secondary"
                    onClick={openSignupModal}
                    className="h-12 px-8 text-base font-medium shadow-lg transition-all duration-200 hover:shadow-xl"
                  >
                    {t("ctaSignUpButton")}
                  </Button>
                </div>
                <p className="mt-6 text-sm text-primary-foreground/80">
                  <a
                    href="mailto:neo@allonia.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-4 transition-colors duration-200 hover:text-primary-foreground"
                  >
                    {t("ctaContactUsLink")}
                  </a>
                </p>
              </div>
            </div>
          </section>
        </main>

        {/* Fixed Footer - Always visible */}
        <footer className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          {/* Gradient fade instead of solid border */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
          <div className="container mx-auto px-2 py-3 sm:px-4">
            <div className="flex flex-row items-center justify-between gap-3 sm:gap-4">
              <p className="text-xs text-muted-foreground">
                © {currentYear}{" "}
                <a
                  href="https://www.allonia.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline-offset-4 transition-colors duration-200 hover:text-foreground hover:underline"
                >
                  Allonia
                </a>
                . {t("footerCopyright")}
              </p>
              <nav className="flex gap-3 sm:gap-6">
                <Link
                  className="text-xs text-muted-foreground underline-offset-4 transition-colors duration-200 hover:text-foreground hover:underline sm:text-sm"
                  href={`/${locale}/conditions-utilisation`}
                  target="_blank"
                >
                  {t("footerTermsLink")}
                </Link>
                <Link
                  className="text-xs text-muted-foreground underline-offset-4 transition-colors duration-200 hover:text-foreground hover:underline sm:text-sm"
                  href={`/${locale}/politique-confidentialite`}
                  target="_blank"
                >
                  {t("footerPrivacyLink")}
                </Link>
              </nav>
            </div>
          </div>
        </footer>
      </div>

      {/* Signup Modal */}
      <Dialog open={showSignupModal} onOpenChange={handleSignupModalOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <VisuallyHidden>
            <DialogTitle>{t("signupTitle") || "Sign Up"}</DialogTitle>
            <DialogDescription>
              {t("signupDescription") || "Create a new account"}
            </DialogDescription>
          </VisuallyHidden>
          <SignupForm isModal={true} openLoginModal={openLoginModal} />
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog
        open={showForgotPasswordModal}
        onOpenChange={handleForgotPasswordModalOpenChange}
      >
        <DialogContent className="sm:max-w-[425px]">
          <VisuallyHidden>
            <DialogTitle>
              {t("forgotPasswordTitle") || "Reset Password"}
            </DialogTitle>
            <DialogDescription>
              {t("forgotPasswordDescription") ||
                "Enter your email to reset your password"}
            </DialogDescription>
          </VisuallyHidden>
          <ForgotPasswordForm isModal={true} openLoginModal={openLoginModal} />
        </DialogContent>
      </Dialog>
    </>
  );
}
